import { create } from 'zustand';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAppStore = create((set, get) => ({
  // Data
  lines: [],
  stations: [],
  personnel: [],
  personnelAssignments: {},
  categories: [],
  customers: [],
  seatTypes: [],
  projects: [],
  operations: [],
  shifts: [],
  capacity: null,
  productionResult: null,
  
  // UI State (persistent across navigation)
  loading: false,
  error: null,
  selectedLine: null,
  selectedStation: null,
  selectedProject: null,
  focusedLineId: null,
  
  // Per-line persistent state
  lineConfigs: {}, // { [lineId]: { selProject, selCategory, selCustomer, overtimeMin, productionResult, completedUnits } }
  lineActiveState: {}, // { [lineId]: boolean }
  prepTablesActive: true,
  lineResults: {}, // { [lineId]: result } for multi-line summary
  lineSimCompleted: {}, // { [lineId]: count } for real-time sim completed units

  // Global simulation state (for "Calculate All Active")
  globalSimState: 'idle', // 'idle' | 'calculating' | 'running' | 'paused' | 'finished'
  globalSimSpeed: 100,
  globalSimTime: 0,
  prepTableSimCompleted: {}, // { [tableId]: count } for prep table simulation
  prepTableOvertime: {}, // { [tableId]: minutes }

  // Actions
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setSelectedLine: (line) => set({ selectedLine: line }),
  setSelectedStation: (station) => set({ selectedStation: station }),
  setSelectedProject: (project) => set({ selectedProject: project }),
  setFocusedLineId: (lineId) => set({ focusedLineId: lineId }),

  // Per-line config
  updateLineConfig: (lineId, updates) => set(state => ({
    lineConfigs: { ...state.lineConfigs, [lineId]: { ...(state.lineConfigs[lineId] || {}), ...updates } }
  })),
  getLineConfig: (lineId) => get().lineConfigs[lineId] || { selProject: '', selCategory: '', selCustomer: '', overtimeMin: 0, productionResult: null, completedUnits: 0 },
  
  toggleLineActive: async (lineId) => {
    const isCurrentlyActive = get().lineActiveState[lineId] ?? true;
    // If turning OFF → clear stations on backend (release personnel)
    if (isCurrentlyActive) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await axios.post(`${API_URL}/api/lines/${lineId}/clear`);
        await get().fetchStations();
        await get().fetchPersonnelAssignments();
      } catch (err) { console.error('Failed to clear line:', err); }
    }
    set(state => ({
      lineActiveState: { ...state.lineActiveState, [lineId]: !isCurrentlyActive }
    }));
  },
  isLineActive: (lineId) => get().lineActiveState[lineId] ?? true,

  clearLine: async (lineId) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/lines/${lineId}/clear`);
      await get().fetchStations();
      await get().fetchPersonnelAssignments();
    } catch (err) { set({ error: err.message }); }
  },
  
  setPrepTablesActive: (active) => set({ prepTablesActive: active }),
  togglePrepTablesActiveState: () => set(state => ({ prepTablesActive: !state.prepTablesActive })),

  setLineResult: (lineId, result) => set(state => ({
    lineResults: { ...state.lineResults, [lineId]: result }
  })),
  clearLineResult: (lineId) => set(state => {
    const newResults = { ...state.lineResults };
    delete newResults[lineId];
    const newSim = { ...state.lineSimCompleted };
    delete newSim[lineId];
    return { lineResults: newResults, lineSimCompleted: newSim };
  }),
  setLineSimCompleted: (lineId, count) => set(state => ({
    lineSimCompleted: { ...state.lineSimCompleted, [lineId]: count }
  })),
  setPrepTableSimCompleted: (tableId, count) => set(state => ({
    prepTableSimCompleted: { ...state.prepTableSimCompleted, [tableId]: count }
  })),
  setGlobalSimState: (state) => set({ globalSimState: state }),
  setGlobalSimSpeed: (speed) => set({ globalSimSpeed: speed }),
  setGlobalSimTime: (time) => set({ globalSimTime: time }),
  resetGlobalSim: () => set({ globalSimState: 'idle', globalSimTime: 0, lineSimCompleted: {}, prepTableSimCompleted: {}, lineResults: {} }),
  setPrepTableOvertime: (tableId, minutes) => set(state => ({
    prepTableOvertime: { ...state.prepTableOvertime, [tableId]: minutes }
  })),

  // API Calls - Lines
  fetchLines: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/lines`);
      set({ lines: res.data });
    } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Stations
  fetchStations: async (lineId = null) => {
    try {
      const url = lineId ? `${API_URL}/api/stations?line_id=${lineId}` : `${API_URL}/api/stations`;
      const res = await axios.get(url);
      if (lineId) {
        // Merge with existing stations for other lines
        set(state => {
          const otherStations = state.stations.filter(s => s.line_id !== lineId);
          return { stations: [...otherStations, ...res.data] };
        });
      } else {
        set({ stations: res.data });
      }
    } catch (err) { set({ error: err.message }); }
  },

  assignPersonnelToStation: async (stationId, personnelId) => {
    await axios.put(`${API_URL}/api/stations/${stationId}/assign-personnel`, { station_id: stationId, personnel_id: personnelId });
    await get().fetchStations();
    await get().fetchPersonnelAssignments();
  },

  unassignPersonnelFromStation: async (stationId) => {
    await axios.put(`${API_URL}/api/stations/${stationId}/unassign-personnel`);
    await get().fetchStations();
    await get().fetchPersonnelAssignments();
  },

  // API Calls - Personnel
  fetchPersonnel: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/personnel`);
      set({ personnel: res.data });
    } catch (err) { set({ error: err.message }); }
  },

  fetchPersonnelAssignments: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/personnel-assignments`);
      set({ personnelAssignments: res.data });
    } catch (err) { set({ error: err.message }); }
  },
  createPersonnel: async (data) => {
    try { await axios.post(`${API_URL}/api/personnel`, data); await get().fetchPersonnel(); } catch (err) { set({ error: err.message }); }
  },
  updatePersonnel: async (id, data) => {
    try { await axios.put(`${API_URL}/api/personnel/${id}`, data); await get().fetchPersonnel(); } catch (err) { set({ error: err.message }); }
  },
  deletePersonnel: async (id) => {
    try { await axios.delete(`${API_URL}/api/personnel/${id}`); await get().fetchPersonnel(); await get().fetchStations(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Categories
  fetchCategories: async () => {
    try { const res = await axios.get(`${API_URL}/api/categories`); set({ categories: res.data }); } catch (err) { set({ error: err.message }); }
  },
  createCategory: async (data) => {
    try { await axios.post(`${API_URL}/api/categories`, data); await get().fetchCategories(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Customers
  fetchCustomers: async (categoryId = null) => {
    try {
      const url = categoryId ? `${API_URL}/api/customers?category_id=${categoryId}` : `${API_URL}/api/customers`;
      const res = await axios.get(url); set({ customers: res.data });
    } catch (err) { set({ error: err.message }); }
  },
  createCustomer: async (data) => {
    try { await axios.post(`${API_URL}/api/customers`, data); await get().fetchCustomers(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Seat Types
  fetchSeatTypes: async (customerId = null, categoryId = null) => {
    try {
      let url = `${API_URL}/api/seat-types`;
      const params = [];
      if (customerId) params.push(`customer_id=${customerId}`);
      if (categoryId) params.push(`category_id=${categoryId}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url); set({ seatTypes: res.data });
    } catch (err) { set({ error: err.message }); }
  },
  createSeatType: async (data) => {
    try { await axios.post(`${API_URL}/api/seat-types`, data); await get().fetchSeatTypes(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Projects
  fetchProjects: async (seatTypeId = null, lineId = null) => {
    try {
      let url = `${API_URL}/api/projects`;
      const params = [];
      if (seatTypeId) params.push(`seat_type_id=${seatTypeId}`);
      if (lineId) params.push(`line_id=${lineId}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url); set({ projects: res.data });
    } catch (err) { set({ error: err.message }); }
  },
  createProject: async (data) => {
    try { const res = await axios.post(`${API_URL}/api/projects`, data); await get().fetchProjects(); return res.data; } catch (err) { set({ error: err.message }); }
  },
  updateProject: async (id, data) => {
    try { await axios.put(`${API_URL}/api/projects/${id}`, data); await get().fetchProjects(); } catch (err) { set({ error: err.message }); }
  },
  deleteProject: async (id) => {
    try { await axios.delete(`${API_URL}/api/projects/${id}`); await get().fetchProjects(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Operations
  fetchOperations: async (projectId = null, stationId = null) => {
    try {
      let url = `${API_URL}/api/operations`;
      const params = [];
      if (projectId) params.push(`project_id=${projectId}`);
      if (stationId) params.push(`station_id=${stationId}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url); set({ operations: res.data });
    } catch (err) { set({ error: err.message }); }
  },
  createOperation: async (data) => {
    try { await axios.post(`${API_URL}/api/operations`, data); await get().fetchOperations(data.project_id); await get().fetchStations(); } catch (err) { set({ error: err.message }); }
  },
  updateOperation: async (id, data) => {
    try { await axios.put(`${API_URL}/api/operations/${id}`, data); await get().fetchOperations(data.project_id); await get().fetchStations(); } catch (err) { set({ error: err.message }); }
  },
  deleteOperation: async (id, projectId) => {
    try { await axios.delete(`${API_URL}/api/operations/${id}`); await get().fetchOperations(projectId); await get().fetchStations(); } catch (err) { set({ error: err.message }); }
  },
  assignOperationToStation: async (operationId, stationId, projectId) => {
    try { await axios.put(`${API_URL}/api/operations/${operationId}/assign-station`, { operation_id: operationId, station_id: stationId }); await get().fetchOperations(projectId); await get().fetchStations(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Shifts
  fetchShifts: async () => {
    try { const res = await axios.get(`${API_URL}/api/shifts`); set({ shifts: res.data }); } catch (err) { set({ error: err.message }); }
  },
  createShift: async (data) => {
    try { await axios.post(`${API_URL}/api/shifts`, data); await get().fetchShifts(); await get().fetchCapacity(); } catch (err) { set({ error: err.message }); }
  },
  updateShift: async (id, data) => {
    try { await axios.put(`${API_URL}/api/shifts/${id}`, data); await get().fetchShifts(); await get().fetchCapacity(); } catch (err) { set({ error: err.message }); }
  },
  deleteShift: async (id) => {
    try { await axios.delete(`${API_URL}/api/shifts/${id}`); await get().fetchShifts(); } catch (err) { set({ error: err.message }); }
  },

  // API Calls - Capacity
  fetchCapacity: async (shiftId = null) => {
    try {
      const url = shiftId ? `${API_URL}/api/capacity?shift_id=${shiftId}` : `${API_URL}/api/capacity`;
      const res = await axios.get(url); set({ capacity: res.data });
    } catch (err) { set({ error: err.message }); }
  },

  // Production Calculation
  calculateProduction: async (projectId, shiftId, personnelCount) => {
    try {
      const res = await axios.post(`${API_URL}/api/calculate-production`, { project_id: projectId, shift_id: shiftId, personnel_count: personnelCount });
      set({ productionResult: res.data }); return res.data;
    } catch (err) { set({ error: err.message }); }
  },
  clearProductionResult: () => set({ productionResult: null }),

  // Fetch All
  fetchAll: async () => {
    set({ loading: true });
    try {
      await Promise.all([
        get().fetchLines(), get().fetchStations(), get().fetchPersonnel(),
        get().fetchCategories(), get().fetchCustomers(), get().fetchSeatTypes(),
        get().fetchProjects(), get().fetchOperations(), get().fetchShifts(),
        get().fetchCapacity(), get().fetchPrepTables(), get().fetchPersonnelAssignments(),
      ]);
    } finally { set({ loading: false }); }
  },

  seedData: async () => {
    try { await axios.post(`${API_URL}/api/seed`); await get().fetchAll(); } catch (err) { set({ error: err.message }); }
  },
  resetData: async () => {
    try { await axios.post(`${API_URL}/api/reset`); await get().fetchAll(); } catch (err) { set({ error: err.message }); }
  },

  // Prep Tables
  prepTables: [],
  fetchPrepTables: async () => {
    try { const res = await axios.get(`${API_URL}/api/prep-tables`); set({ prepTables: res.data }); } catch (err) { set({ error: err.message }); }
  },
  togglePrepTableActive: async (tableId) => {
    try { await axios.put(`${API_URL}/api/prep-tables/${tableId}/toggle-active`); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  assignPrepTablePersonnel: async (tableId, personnelId, action = 'add') => {
    await axios.post(`${API_URL}/api/prep-tables/${tableId}/assign-personnel`, { personnel_id: personnelId, action });
    await get().fetchPrepTables();
    await get().fetchPersonnelAssignments();
  },
  addPrepTableOperation: async (tableId, name, durationSec) => {
    try { await axios.post(`${API_URL}/api/prep-tables/${tableId}/operations`, { name, duration_sec: durationSec }); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  deletePrepTableOperation: async (tableId, opId) => {
    try { await axios.delete(`${API_URL}/api/prep-tables/${tableId}/operations/${opId}`); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  clearPrepTable: async (tableId) => {
    try { await axios.post(`${API_URL}/api/prep-tables/${tableId}/clear`); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  addPrepTable: async () => {
    try { await axios.post(`${API_URL}/api/prep-tables`); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  removePrepTable: async (tableId) => {
    try { await axios.delete(`${API_URL}/api/prep-tables/${tableId}`); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  updatePrepTableOperation: async (tableId, opId, data) => {
    try { await axios.put(`${API_URL}/api/prep-tables/${tableId}/operations/${opId}`, data); await get().fetchPrepTables(); } catch (err) { set({ error: err.message }); }
  },
  renamePrepTable: async (tableId, name) => {
    try { await axios.put(`${API_URL}/api/prep-tables/${tableId}/rename`, { name }); await get().fetchPrepTables(); await get().fetchLines(); } catch (err) { set({ error: err.message }); }
  },

  // Project Actions (unified - no templates)
  applyProject: async (projectId) => {
    try {
      const res = await axios.post(`${API_URL}/api/projects/${projectId}/apply`);
      await get().fetchStations();
      await get().fetchPersonnelAssignments();
      return res.data;
    } catch (err) { set({ error: err.message }); }
  },
  saveProjectState: async (projectId) => {
    try {
      const res = await axios.put(`${API_URL}/api/projects/${projectId}/save-state`);
      return res.data;
    } catch (err) { set({ error: err.message }); }
  },
  createProjectFromLine: async (data) => {
    try {
      const res = await axios.post(`${API_URL}/api/projects/create-from-line`, data);
      await get().fetchProjects();
      return res.data;
    } catch (err) { set({ error: err.message }); }
  },
  clearAllCarousels: async () => {
    try {
      const res = await axios.post(`${API_URL}/api/lines/clear-all-carousels`);
      await get().fetchStations();
      await get().fetchPersonnelAssignments();
      return res.data;
    } catch (err) { set({ error: err.message }); }
  },
}));

export default useAppStore;
